/**
 * Customer Groups Manager
 * Ügyfélcsoportok kezelése
 */

import React, { useState, useEffect } from 'react';
import { useCustomerGroups } from '../../hooks/useCustomerGroups';
import { CustomerGroup } from '../../types/customerGroup';
import CustomerGroupEditor from './CustomerGroupEditor';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import GroupMembersModal from './GroupMembersModal';
import toast from 'react-hot-toast';
import {
  UserGroupIcon,
  PencilSquareIcon,
  TrashIcon,
  UsersIcon,
  ChartBarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const CustomerGroups: React.FC = () => {
  const { groups, loading, error, remove, exportGroup, refresh } = useCustomerGroups();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CustomerGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<CustomerGroup | null>(null);
  const [viewingGroup, setViewingGroup] = useState<CustomerGroup | null>(null);

  // Csoport szerkesztése
  const handleEdit = (group: CustomerGroup) => {
    setEditingGroup(group);
    setIsEditorOpen(true);
  };

  // Csoport törlése - modal megnyitása
  const handleDeleteClick = (group: CustomerGroup) => {
    setGroupToDelete(group);
  };

  // Törlés megerősítése
  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      await remove(groupToDelete.id);
      toast.success('Csoport törölve');
      setGroupToDelete(null);
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    }
  };

  // Csoport exportálása
  const handleExport = async (group: CustomerGroup) => {
    try {
      const customers = await exportGroup(group.id);
      
      // CSV generálás
      const csv = [
        ['Név', 'Email', 'Telefon', 'Státusz'],
        ...customers.map(c => [
          c.name || '',
          c.email || '',
          c.phone || '',
          c.status || ''
        ])
      ].map(row => row.join(',')).join('\n');

      // Letöltés
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${group.name}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      toast.success('Csoport exportálva');
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <p className="text-red-700 dark:text-red-300">Hiba: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Csoportok lista */}
      {groups.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            Nincs még csoport
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Kezdd el egy új ügyfélcsoport létrehozásával a fenti "Új csoport" gombbal.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop táblázat nézet */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Csoport
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ügyfelek
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Kampányok
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Címkék
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Műveletek
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {groups.map((group) => (
                    <tr 
                      key={group.id} 
                      onClick={() => setViewingGroup(group)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 shadow-md ring-2 ring-white dark:ring-gray-800 transform hover:scale-110 transition-transform duration-200"
                            style={{ 
                              background: `linear-gradient(135deg, ${group.color || '#3B82F6'}40, ${group.color || '#3B82F6'}20)`,
                              backdropFilter: 'blur(10px)'
                            }}
                          >
                            <span className="drop-shadow-sm">{group.icon || '👥'}</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {group.name}
                            </div>
                            {group.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                {group.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                          <UsersIcon className="h-5 w-5 text-gray-400 mr-2" />
                          {group.customerCount}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                          <ChartBarIcon className="h-5 w-5 text-gray-400 mr-2" />
                          {group.campaignCount || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {group.tags && group.tags.length > 0 ? (
                            group.tags.slice(0, 3).map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                          {group.tags && group.tags.length > 3 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              +{group.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingGroup(group);
                            }}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Tagok megtekintése"
                          >
                            <UsersIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(group);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                            title="Szerkesztés"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExport(group);
                            }}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            title="Export CSV"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(group);
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Törlés"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobil kártya nézet */}
          <div className="lg:hidden space-y-4">
            {groups.map((group) => (
            <div
              key={group.id}
              onClick={() => setViewingGroup(group)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            >
              {/* Csoport header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-md ring-2 ring-white dark:ring-gray-800"
                    style={{ 
                      background: `linear-gradient(135deg, ${group.color || '#3B82F6'}40, ${group.color || '#3B82F6'}20)`,
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <span className="drop-shadow-sm">{group.icon || '👥'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {group.name}
                    </h3>
                    {group.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                        {group.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Statisztika */}
              <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <UsersIcon className="h-4 w-4" />
                    Ügyfelek
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {group.customerCount}
                  </span>
                </div>
                {group.campaignCount !== undefined && group.campaignCount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      <ChartBarIcon className="h-4 w-4" />
                      Kampányok
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {group.campaignCount}
                    </span>
                  </div>
                )}
              </div>

              {/* Címkék - Design System Badge */}
              {group.tags && group.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {group.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                  {group.tags.length > 3 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      +{group.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Műveletek */}
              <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingGroup(group);
                  }}
                  className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md"
                  title="Tagok"
                >
                  <UsersIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(group);
                  }}
                  className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                >
                  <PencilSquareIcon className="h-4 w-4 mr-1" />
                  Szerkeszt
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExport(group);
                  }}
                  className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                  title="Export CSV"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(group);
                  }}
                  className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md"
                  title="Törlés"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            ))}
          </div>
        </>
      )}

      {/* Editor Modal */}
      {isEditorOpen && (
        <CustomerGroupEditor
          group={editingGroup || undefined}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingGroup(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!groupToDelete}
        onClose={() => setGroupToDelete(null)}
        onConfirm={handleConfirmDelete}
        title={`Csoport törlése: ${groupToDelete?.name || ''}`}
        deleteMessage={`Biztosan törölni szeretnéd a(z) "${groupToDelete?.name}" csoportot?`}
        deleteWarning="A csoport törlése után az ügyfelek nem kerülnek törlésre, csak a csoport megszűnik."
      />

      {/* Group Members Modal */}
      <GroupMembersModal
        group={viewingGroup ? groups.find(g => g.id === viewingGroup.id) || viewingGroup : null}
        onClose={() => setViewingGroup(null)}
      />
    </div>
  );
};

export default CustomerGroups;
